import { useNavigate } from '@tanstack/react-router';
import { CalendarClock } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useAuthStore } from '@/stores/authStore';

import { useCopyDayBlocks } from '../../hooks/useCopyDayBlocks';
import { useCadenceStore } from '../../store/cadenceStore';

/**
 * Active-redirect modal: a psychologist with undecided days in the next 14
 * is forced through one of two resolutions before they can continue. The
 * modal cannot be dismissed (no `onOpenChange` action, no X) — that's
 * intentional, the user must either commit a day-by-day plan or stamp
 * everything OFF as leave.
 *
 * The body presents each undecided day as a small typeset card so the
 * scope of "what's missing" is legible at a glance, instead of a bullet
 * list of opaque ISO strings the user has to mentally decode.
 */
export function CadenceOverdueModal() {
  const open = useCadenceStore((s) => s.open);
  const missingDays = useCadenceStore((s) => s.missingDays);
  const closeModal = useCadenceStore((s) => s.closeModal);
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const copy = useCopyDayBlocks(user?.id ?? '');

  const handleUpdateDayByDay = () => {
    closeModal();
    void navigate({ to: '/app/availability' });
  };

  const handleMarkAllOff = async () => {
    if (!user?.id) return;
    // The mutation swallows per-date failures and always resolves, so the
    // outcome — not the absence of a throw — is what says whether the gate is
    // actually clear. Closing unconditionally used to dismiss this
    // non-dismissible gate even when every write failed, leaving the user still
    // overdue with only a toast. Any remaining day means still overdue: keep
    // the modal up so they can retry.
    const outcome = await copy.mutateAsync({
      sourceBlocks: [{ startTime: '00:00', endTime: '24:00', mode: null, reason: 'On leave' }],
      targetDates: missingDays,
    });
    if (outcome.failed.length === 0) {
      closeModal();
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        // Active redirect contract: cannot dismiss without resolving.
        if (!o) {
          // no-op: keep modal open
        }
      }}
    >
      <DialogContent hideClose className="max-w-md gap-0 overflow-hidden p-0">
        <div className="flex flex-col gap-4 px-6 pt-6 pb-5">
          <DialogHeader className="flex-row items-start gap-3 space-y-0">
            <span
              aria-hidden="true"
              className="bg-primary-50 ring-primary-100 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ring-1"
            >
              <CalendarClock className="text-primary-700 h-5 w-5" strokeWidth={1.5} />
            </span>
            <div className="flex min-w-0 flex-col gap-1">
              <DialogTitle>Update your availability</DialogTitle>
              <DialogDescription>
                {String(missingDays.length)} of the next 14 days are undecided. Choose how to fill
                them in.
              </DialogDescription>
            </div>
          </DialogHeader>

          {/* Day cards — each undecided day as a typeset tile so the user can
              scan the scope at a glance instead of decoding ISO strings. */}
          <ul aria-label="Days needing a decision" className="flex flex-wrap gap-2">
            {missingDays.map((iso) => (
              <li key={iso}>
                <DayCard iso={iso} />
              </li>
            ))}
          </ul>
        </div>

        {/* Footer sits on the same white surface as the body — a colored
            band would fight the warm day cards and create a corner-clip
            mismatch with the dialog's rounded-xl. The hairline divider
            alone reads as "this is the action zone" without competing
            with the visual hierarchy above. */}
        <div className="border-border flex items-center justify-end gap-2 border-t px-5 py-4">
          <Button variant="ghost" onClick={() => void handleMarkAllOff()} disabled={copy.isPending}>
            Mark all missing days OFF
          </Button>
          <Button variant="primary" onClick={handleUpdateDayByDay} disabled={copy.isPending}>
            Update day-by-day
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/**
 * One undecided day, rendered as a three-line typeset tile: uppercase
 * weekday eyebrow on a slightly darker amber band, large display-font
 * day number, month abbreviation. The tile carries a soft amber ring +
 * fill (defined warning shades only — `200` / `800` are not in the
 * project's @theme block and silently fall back to currentColor).
 *
 * `title={iso}` keeps the canonical ISO string in the DOM for tooltips
 * and tests, even though the visible content is the formatted parts.
 */
function DayCard({ iso }: { iso: string }) {
  // Anchor at noon UTC so DST shifts can't tip the date into the day
  // before/after when the user's locale is east of UTC.
  const d = new Date(`${iso}T12:00:00Z`);
  const weekday = d.toLocaleDateString(undefined, { weekday: 'short' });
  const day = d.toLocaleDateString(undefined, { day: 'numeric' });
  const month = d.toLocaleDateString(undefined, { month: 'short' });

  return (
    <div
      title={iso}
      className="ring-warning-300/50 bg-warning-50/60 flex w-16 flex-col overflow-hidden rounded-lg ring-1"
    >
      <span className="bg-warning-300/30 text-warning-700 px-2 py-0.5 text-center text-[0.625rem] font-semibold tracking-widest uppercase">
        {weekday}
      </span>
      <span className="text-accent-ink font-display py-1.5 text-center text-2xl leading-none tracking-tight">
        {day}
      </span>
      <span className="text-meta-sm pb-1.5 text-center text-neutral-600 uppercase">{month}</span>
    </div>
  );
}
